/**
 * Modal for adding or editing the staff-only note of an existing appointment.
 */

import { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { StructuredNoteField } from '@/components/structured-note';
import type { Appointment } from '@/api/mcp-appointments';
import { formatAppointmentTimeRange } from '../calendar/calendarUtils';
import { formModalWidth } from '@/components/ui/formModalStyles';
import { FormModalFooter } from '@/components/ui/FormModalFooter';
import { ModalTitle } from '@/components/ui/ModalTitle';


/** Max note length, matching the create-appointment notes field. */
const NOTE_MAX_LENGTH = 4000;

interface AppointmentNoteEditModalProps {
  open: boolean;
  appointment: Appointment;
  /** Client display name shown in the subtitle. */
  clientName: string;
  saving: boolean;
  /** Renders above the floating details popup, which sits over regular modals. */
  zIndex?: number;
  onCancel: () => void;
  /** Persists the note; resolves `true` when the modal may close. */
  onSave: (notes: string) => Promise<boolean>;
}

/** Edits an appointment note with the same structured note field used when creating appointments. */
export function AppointmentNoteEditModal({
  open,
  appointment,
  clientName,
  saving,
  zIndex,
  onCancel,
  onSave
}: AppointmentNoteEditModalProps) {
  const { t, i18n } = useTranslation('manage');
  const [draft, setDraft] = useState(appointment.notes ?? '');

  // Start from the stored note every time the modal opens.
  useEffect(() => {
    if (open) {
      setDraft(appointment.notes ?? '');
    }
  }, [open, appointment.id, appointment.notes]);

  const isUnchanged = draft === (appointment.notes ?? '');

  const handleSave = async () => {
    const saved = await onSave(draft);
    if (saved) {
      onCancel();
    }
  };

  return (
    <Modal
      open={open}
      zIndex={zIndex}
      footer={null}
      onCancel={saving ? undefined : onCancel}
      mask={{ closable: !saving }}
      width={formModalWidth(640)}
      destroyOnHidden
      title={
        <ModalTitle
          icon={<FileTextOutlined />}
          title={t('appointmentComponents.details.notesSection.modalTitle')}
          subtitle={t('appointmentComponents.details.notesSection.modalSubtitle', {
            client: clientName,
            date: formatAppointmentTimeRange(appointment.startTime, appointment.endTime, i18n.language)
          })}
        />
      }
    >
      <StructuredNoteField
        value={draft}
        onChange={setDraft}
        rows={6}
        maxLength={NOTE_MAX_LENGTH}
        showCount
        disabled={saving}
        placeholder={t('appointmentComponents.details.notesSection.placeholder')}
      />
      <FormModalFooter
        onCancel={onCancel}
        onSubmit={handleSave}
        submitLabel={t('appointmentComponents.details.notesSection.save')}
        loading={saving}
        submitDisabled={isUnchanged}
      />
    </Modal>
  );
}
